/**
 * ListStatus — the one non-loading terminal state for any list surface.
 *
 * A list that came back with zero rows and a list whose request failed look
 * identical unless the screen is told them apart, so `variant` is required:
 *
 * - `error` — the request failed. Announced with `role="alert"`, and the action
 *   defaults to Retry.
 * - `empty` — the request succeeded and there is genuinely nothing to show. Not
 *   announced, and the action (optional) defaults to Refresh.
 *
 * Only pass `onAction` for content the member can meaningfully re-fetch. Search
 * and filter results should not offer it: rerunning the same query changes
 * nothing, the member has to change the query.
 */
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Button } from './Button';
import './listStatus.css';

export type ListStatusVariant = 'error' | 'empty';

export interface ListStatusProps {
  variant: ListStatusVariant;
  /** Headline. Defaults to generic failure copy for `error`. */
  title?: string;
  /** Supporting line under the title. */
  message?: string;
  onAction?: () => void;
  /** Overrides the default Retry / Refresh label. */
  actionLabel?: string;
  /** Shows the pending label and blocks repeat presses while a retry runs. */
  busy?: boolean;
  /** Rendered above the copy — an illustration or icon for empty states. */
  icon?: React.ReactNode;
  /** `inline` is the compact end-of-list form used for pagination failures. */
  layout?: 'block' | 'inline';
  className?: string;
}

export function ListStatus({
  variant,
  title,
  message,
  onAction,
  actionLabel,
  busy = false,
  icon,
  layout = 'block',
  className = '',
}: ListStatusProps) {
  const { t } = useTranslation();
  const isError = variant === 'error';

  const resolvedTitle = title ?? (isError ? t('common.listStatus.errorTitle') : '');
  const resolvedMessage =
    message ?? (isError ? t('common.listStatus.errorMessage') : '');
  const resolvedActionLabel =
    actionLabel ?? (isError ? t('common.buttons.retry') : t('common.buttons.refresh'));

  const classes = [
    'list-status',
    `list-status--${variant}`,
    `list-status--${layout}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} role={isError ? 'alert' : undefined}>
      {icon && (
        <span className="list-status__icon" aria-hidden>
          {icon}
        </span>
      )}
      {resolvedTitle && <p className="list-status__title">{resolvedTitle}</p>}
      {resolvedMessage && <p className="list-status__message">{resolvedMessage}</p>}
      {onAction && (
        <Button
          size="sm"
          variant="primary"
          onClick={onAction}
          disabled={busy}
        >
          {busy ? t('common.listStatus.busy') : resolvedActionLabel}
        </Button>
      )}
    </div>
  );
}
